// HostelAttendance.js - Hostel Attendance Module for Authorized Representatives
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  Tooltip,
  Stack,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SecurityIcon from "@mui/icons-material/Security";
import DateRangeIcon from "@mui/icons-material/DateRange";
import SendIcon from "@mui/icons-material/Send";

import { API_URL } from "./config";

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro", "Helvetica Neue", Arial, sans-serif';

const HostelAttendance = () => {
  const token = localStorage.getItem("auth");
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG, fontFamily: FONT_FAMILY };
  const rootPaperSx = { mx: "auto", borderRadius: 2, overflow: "hidden", border: "1px solid rgba(25,118,210,0.15)", backgroundColor: "transparent", fontFamily: FONT_FAMILY, "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiTableCell-root, & .MuiInputBase-root, & .MuiInputLabel-root": { fontFamily: FONT_FAMILY } };
  const headerSx = { px: 2, py: 1.5, background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`, color: "white", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = { borderRadius: 2, backgroundColor: "#fff", border: `1px solid ${BORDER}` };
  const sectionSx = { ...surfaceSx, p: 2 };
  const inputSx = { fontFamily: FONT_FAMILY, "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS }, "& .MuiInputLabel-root": { fontFamily: FONT_FAMILY }, "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE }, "& .MuiSelect-select": { fontFamily: FONT_FAMILY } };
  const primaryBtnSx = { fontFamily: FONT_FAMILY, borderRadius: 2, textTransform: "none", fontWeight: 900, backgroundColor: THEME_ORANGE, "&:hover": { backgroundColor: THEME_ORANGE_2 } };
  const theadRowSx = { backgroundColor: "rgba(25,118,210,0.95)", "& th": { fontFamily: FONT_FAMILY, color: "#fff", fontWeight: 900, borderBottom: "1px solid rgba(255,255,255,0.18)", whiteSpace: "nowrap", py: 1 } };
  const zebraRowSx = (idx) => ({ backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff", "&:hover": { backgroundColor: ROW_HOVER }, "& td": { fontFamily: FONT_FAMILY, borderBottom: "1px solid rgba(25,118,210,0.10)", py: 1 } });

  const [loadingRooms, setLoadingRooms] = useState(true);
  const [assignedRooms, setAssignedRooms] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState("");

  // Hostel attendance entry is always for today. Historical dates are handled by reports.
  const attendanceDate = useMemo(() => new Date().toLocaleDateString("en-CA"), []);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Load Assigned Rooms for Logged-in Representative (Requirement 4)
  const fetchMyAssignedRooms = useCallback(async () => {
    try {
      setLoadingRooms(true);
      setError("");
      const res = await axios.get(`${API_URL}/api/HostelAttendance/MyAssignedRooms`, authHeader);

      if (res.data) {
        const rooms = res.data.assignedRooms || [];
        setAssignedRooms(rooms);
        setSelectedBlock("");
        setSelectedFloor("");
        setSelectedRoomId("");
        setStudents([]);
      }
    } catch (err) {
      console.error("Failed to load representative assigned rooms:", err);
      setError(err.response?.data?.error || "Failed to load assigned rooms");
    } finally {
      setLoadingRooms(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchMyAssignedRooms();
  }, [fetchMyAssignedRooms]);

  // Load Room Students when selectedRoomId or attendanceDate changes
  const fetchRoomStudents = useCallback(async (roomId, targetDate) => {
    if (!roomId) return;
    try {
      setLoadingStudents(true);
      setError("");
      const dateQuery = targetDate || attendanceDate;
      const res = await axios.get(`${API_URL}/api/HostelAttendance/RoomStudents/${roomId}?date=${dateQuery}`, authHeader);

      if (res.data?.students) {
        setStudents(res.data.students);
        const submitted = !!res.data.isSubmitted;
        setIsSubmitted(submitted);
        setSubmittedAt(res.data.submittedAt || null);
      }
    } catch (err) {
      console.error("Fetch room students error:", err);
      setError(err.response?.data?.error || "Failed to fetch room students");
    } finally {
      setLoadingStudents(false);
    }
  }, [authHeader, attendanceDate]);

  const blockOptions = useMemo(() =>
    [...new Set(assignedRooms.map((r) => String(r.block || "")).filter(Boolean))].sort(),
    [assignedRooms]
  );

  const floorOptions = useMemo(() =>
    [...new Set(assignedRooms
      .filter((r) => !selectedBlock || String(r.block) === String(selectedBlock))
      .map((r) => String(r.floor ?? ""))
      .filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [assignedRooms, selectedBlock]
  );

  const roomOptions = useMemo(() =>
    assignedRooms.filter((r) =>
      String(r.block || "") === String(selectedBlock || "") &&
      String(r.floor ?? "") === String(selectedFloor || "")
    ),
    [assignedRooms, selectedBlock, selectedFloor]
  );

  const handleBlockChange = (value) => {
    setSelectedBlock(value);
    setSelectedFloor("");
    setSelectedRoomId("");
    setStudents([]);
    setIsSubmitted(false);
    setSubmittedAt(null);
    setError("");
    setSuccessMsg("");
  };

  const handleFloorChange = (value) => {
    setSelectedFloor(value);
    setSelectedRoomId("");
    setStudents([]);
    setIsSubmitted(false);
    setSubmittedAt(null);
    setError("");
    setSuccessMsg("");
  };

  const handleRoomChange = (newRoomId) => {
    setSelectedRoomId(newRoomId || "");
    setSuccessMsg("");
    setError("");
    if (newRoomId) fetchRoomStudents(newRoomId, attendanceDate);
    else setStudents([]);
  };

  const handleStatusChange = (studentKey, newStatus) => {
    setStudents(prev => prev.map(s => s.StudentKey === studentKey ? { ...s, Status: newStatus } : s));
  };

  const handleNotesChange = (studentKey, notes) => {
    setStudents(prev => prev.map(s => s.StudentKey === studentKey ? { ...s, Notes: notes } : s));
  };

  const handleSubmitAttendance = async () => {
    if (!selectedRoomId || students.length === 0) return;

    setSubmitting(true);
    setError("");
    try {
      const selectedRoom = assignedRooms.find(r => String(r.roomId || r.assignmentId) === String(selectedRoomId));

      const payload = {
        roomId: selectedRoomId,
        roomNumber: selectedRoom?.roomNumber || String(selectedRoomId),
        block: selectedRoom?.block || "Hostel",
        attendanceDate,
        attendanceList: students.map(s => {
          const isPresent = s.Status === "Present" || s.Status === 1 || s.Status === "1";
          return {
            StudentKey: s.StudentKey,
            StudentID: s.StudentID,
            StudentName: s.StudentName,
            Status: isPresent ? 1 : 0,
            Notes: !isPresent ? (s.Notes || "") : "",
          };
        }),
      };

      const res = await axios.post(`${API_URL}/api/HostelAttendance/MarkAttendance`, payload, authHeader);
      setSuccessMsg(res.data?.message || "Hostel attendance marked successfully!");
      setIsSubmitted(true);
      setSubmittedAt(res.data?.submittedAt || new Date().toISOString());

      // Immediately re-fetch from database to ensure table state and persistence are 100% in sync
      await fetchRoomStudents(selectedRoomId, attendanceDate);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit attendance");
    } finally {
      setSubmitting(false);
    }
  };

  const attendanceStats = useMemo(() => {
    const present = students.filter((s) => s.Status === "Present" || s.Status === 1 || s.Status === "1").length;
    return { total: students.length, present, absent: students.length - present };
  }, [students]);

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* Header */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2, display: "flex", alignItems: "center", gap: 1 }}>
              <MeetingRoomIcon fontSize="small" /> Hostel Room Attendance
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, opacity: 0.9, fontSize: "0.85rem", mt: 0.5 }}>
              Hostel attendance scoped to your active hostel role assignments
            </Typography>
          </Box>
          {!isSubmitted && (
            <Button
              variant="contained"
              onClick={handleSubmitAttendance}
              disabled={submitting || students.length === 0}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              sx={primaryBtnSx}
            >
              {submitting ? "Saving..." : "Submit Attendance"}
            </Button>
          )}
        </Box>

        <Box sx={bodySx}>
          {/* Cascading hostel scope selector. Attendance is always for today. */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Select Room
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth disabled={loadingRooms || assignedRooms.length === 0} sx={inputSx}>
                  <InputLabel>Block *</InputLabel>
                  <Select value={selectedBlock} onChange={(e) => handleBlockChange(e.target.value)} label="Block *">
                    {blockOptions.map((block) => <MenuItem key={block} value={block}>{block}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth disabled={!selectedBlock} sx={inputSx}>
                  <InputLabel>Floor *</InputLabel>
                  <Select value={selectedFloor} onChange={(e) => handleFloorChange(e.target.value)} label="Floor *">
                    {floorOptions.map((floor) => <MenuItem key={floor} value={floor}>{floor}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth disabled={!selectedBlock || !selectedFloor} sx={inputSx}>
                  <InputLabel>Room *</InputLabel>
                  <Select value={selectedRoomId} onChange={(e) => handleRoomChange(e.target.value)} label="Room *">
                    {roomOptions.map((rm) => (
                      <MenuItem key={rm.roomId || rm.assignmentId} value={rm.roomId || rm.assignmentId}>
                        {rm.roomNumber} ({rm.roomType || "Standard"})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Typography sx={{ fontFamily: FONT_FAMILY, display: "block", mt: 1.25, opacity: 0.85, fontSize: "0.8rem" }}>
              Attendance Date: <strong>{attendanceDate}</strong> (today)
            </Typography>
          </Paper>

          {/* Error / Success Notifications */}
          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {successMsg && (
            <Alert severity="success" onClose={() => setSuccessMsg("")} sx={{ mb: 3, borderRadius: 2 }}>
              {successMsg}
            </Alert>
          )}

          {/* Attendance Marking Table */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: "rgba(25,118,210,0.05)", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                  Room Students Attendance Table
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip label={`Total: ${attendanceStats.total}`} size="small" />
                  <Chip label={`Present: ${attendanceStats.present}`} size="small" />
                  <Chip label={`Absent: ${attendanceStats.absent}`} size="small" sx={{ color: THEME_ORANGE }} />
                </Stack>
                {isSubmitted && (
                  <Chip
                    size="small"
                    icon={<LockIcon fontSize="small" />}
                    label={
                      submittedAt
                        ? `Attendance Submitted & Locked (${new Date(submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
                        : "Attendance Submitted & Locked"
                    }
                    color="success"
                    sx={{ fontWeight: 800 }}
                  />
                )}
              </Box>
              <Typography sx={{ fontFamily: FONT_FAMILY, opacity: 0.85, fontSize: "0.8rem" }}>
                Date: <strong>{attendanceDate}</strong>
              </Typography>
            </Box>

            {loadingStudents ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress size={32} />
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  Loading students for selected room...
                </Typography>
              </Box>
            ) : students.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                Select Block, Floor and Room to load the students currently assigned to that room.
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow sx={theadRowSx}>
                      <TableCell>#</TableCell>
                      <TableCell>Student ID</TableCell>
                      <TableCell>Student Name</TableCell>
                      <TableCell>Branch / Semester</TableCell>
                      <TableCell align="center">Attendance Status</TableCell>
                      <TableCell>Notes / Remarks</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {students.map((s, idx) => (
                      <TableRow key={s.StudentKey || idx} sx={zebraRowSx(idx)}>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 36,
                              height: 30,
                              px: 1,
                              borderRadius: 999,
                              backgroundColor: "rgba(25,118,210,0.12)",
                              color: "rgba(25,118,210,0.95)",
                              fontWeight: 900,
                              fontSize: "0.9rem",
                            }}
                          >
                            {idx + 1}
                          </Box>
                        </TableCell>
                        <TableCell>{s.StudentID}</TableCell>
                        <TableCell sx={{ fontWeight: "800 !important" }}>{s.StudentName}</TableCell>
                        <TableCell>{s.BranchCode} (Sem {s.Semester})</TableCell>
                        <TableCell align="center">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={s.Status === "Present" || s.Status === 1 || s.Status === "1"}
                                onChange={(e) => handleStatusChange(s.StudentKey, e.target.checked ? "Present" : "Absent")}
                                disabled={isSubmitted}
                                color="success"
                              />
                            }
                            label={
                              <Chip
                                size="small"
                                label={(s.Status === "Present" || s.Status === 1 || s.Status === "1") ? "Present" : "Absent"}
                                color={(s.Status === "Present" || s.Status === 1 || s.Status === "1") ? "success" : "error"}
                                variant={(s.Status === "Present" || s.Status === 1 || s.Status === "1") ? "outlined" : "filled"}
                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 400, cursor: isSubmitted ? "default" : "pointer" }}
                              />
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {!(s.Status === "Present" || s.Status === 1 || s.Status === "1") ? (
                            <TextField
                              size="small"
                              placeholder="Enter reason / remarks for absence..."
                              value={s.Notes || ""}
                              onChange={(e) => handleNotesChange(s.StudentKey, e.target.value)}
                              disabled={isSubmitted}
                              fullWidth
                              sx={{ "& .MuiInputBase-input": { fontFamily: FONT_FAMILY, fontWeight: 400 } }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default HostelAttendance;
